import { Group, Menu, Text, Tooltip } from '@mantine/core';
import {
  IconArchive,
  IconBackspace,
  IconCopy,
  IconDotsVertical,
  IconEdit,
  IconEye,
  IconLink,
  IconPlayerPlay,
  IconRefresh,
  IconTrashX,
  IconUnlink,
} from '@tabler/icons-react';
import { IRowNode } from 'ag-grid-community';
import { useApi } from 'api/api-context';
import ActionIcon from 'components/ActionIcon';
import Button from 'components/Button';
import { useConfirm } from 'components/modals/message/MessageProvider';
import panic from 'errors/panic';
import { SINGLE_ACTION_BUTTON_WIDTH, useResponsiveDesign } from 'hooks/use-responsive-design';
import { Children, ReactNode, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { HR_COPY_PERSON_PAGE_PATH, HR_EDIT_PERSON_PAGE_PATH, HR_TERM_PAGE_PATH } from 'routes/paths';

interface PrimaryAction {
  label: string;
  icon: ReactNode;
  disabled?: boolean;
  onClick?: () => void;
  to?: string;
}

interface ResponsiveHrActionsProps {
  primary: PrimaryAction;
  ariaLabel: string;
  menuLabel: string;
  children?: ReactNode;
}

/** Gives AG Grid a changing value so synthetic action cells refresh with authoritative row state. */
export function hrActionCellVersion({ data }: { data: any }) {
  return data?.version ?? data?.status ?? data?.personId;
}

/** Returns whether a row with an optional lifecycle status is active. */
function isActiveRow(data: any) {
  const status = data?.rawStatus ?? data?.status;
  return status === undefined || status === true || status === 1 || status === '1' || status === 'active';
}

/** Shared responsive layout used by each dedicated HR row action component. */
function ResponsiveHrActions({ primary, ariaLabel, menuLabel, children }: ResponsiveHrActionsProps) {
  const { tableActionMode, isMobile } = useResponsiveDesign();
  const menuOnly = tableActionMode === 'mobile';
  const compact = tableActionMode === 'tablet';
  const secondaryActions = Children.toArray(children).filter(Boolean);
  const hasSecondary = secondaryActions.length > 0;

  return (
    <Group spacing={12} noWrap>
      {!menuOnly &&
        (compact ? (
          <Tooltip label={primary.label} events={{ hover: true, focus: true, touch: true }}>
            {primary.to && !primary.disabled ? (
              <ActionIcon component={Link} to={primary.to} variant="secondary" size="md">
                {primary.icon}
              </ActionIcon>
            ) : (
              <ActionIcon variant="secondary" size="md" onClick={primary.onClick} disabled={primary.disabled}>
                {primary.icon}
              </ActionIcon>
            )}
          </Tooltip>
        ) : primary.to && !primary.disabled ? (
          <Button
            component={Link}
            to={primary.to}
            variant="secondary"
            size="md"
            leftIcon={primary.icon}
            w={SINGLE_ACTION_BUTTON_WIDTH}
          >
            {primary.label}
          </Button>
        ) : (
          <Button
            variant="secondary"
            size="md"
            leftIcon={primary.icon}
            onClick={primary.onClick}
            disabled={primary.disabled}
            w={SINGLE_ACTION_BUTTON_WIDTH}
          >
            {primary.label}
          </Button>
        ))}
      {(menuOnly || hasSecondary) && (
        <Menu position="bottom-end" withinPortal>
          <Menu.Target>
            <ActionIcon variant="tertiary" size={isMobile ? 'sm' : 'md'} aria-label={ariaLabel}>
              <IconDotsVertical stroke={1.5} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Label>
              <Text maw={160} truncate title={menuLabel}>
                {menuLabel}
              </Text>
            </Menu.Label>
            {menuOnly && (
              <>
                {primary.to && !primary.disabled ? (
                  <Menu.Item icon={primary.icon} component={Link} to={primary.to}>
                    {primary.label}
                  </Menu.Item>
                ) : (
                  <Menu.Item icon={primary.icon} onClick={primary.onClick} disabled={primary.disabled}>
                    {primary.label}
                  </Menu.Item>
                )}
                {hasSecondary && <Menu.Divider />}
              </>
            )}
            {secondaryActions}
          </Menu.Dropdown>
        </Menu>
      )}
    </Group>
  );
}

interface HrPersonActionsProps {
  data: any;
  onStatusChange?: () => void;
}

/** People-table edit/view and confirmed lifecycle actions. */
export function HrPersonActions({ data, onStatusChange }: HrPersonActionsProps) {
  const { getAction } = useApi();
  const { confirm } = useConfirm();
  const canView = Boolean(data?.actionPermissions?.view);
  const canEdit = Boolean(data?.actionPermissions?.edit);
  const active = isActiveRow(data);
  const path = HR_EDIT_PERSON_PAGE_PATH.insert({ personId: String(data?.personId) });
  const copyPath = HR_COPY_PERSON_PAGE_PATH.insert({ personId: String(data?.personId) });
  const personName = `${data?.firstName ?? ''} ${data?.lastName ?? ''}`.trim() || `ID ${data?.personId}`;

  /** Persists a versioned lifecycle transition and reloads the authoritative table. */
  const setStatusRemote = useCallback(
    (status: 'active' | 'inactive') => {
      return getAction('HrSetPersonStatus')({
        parameters: { personId: String(data?.personId) },
        payload: { version: data?.version, status },
      })
        .then(() => onStatusChange?.())
        .catch(panic);
    },
    [data?.personId, data?.version, getAction, onStatusChange]
  );

  /** Opens the shared confirmation modal for a person lifecycle transition. */
  const confirmSetStatus = useCallback(() => {
    const action = active ? 'Vyradiť' : 'Zaradiť';
    confirm({
      title: `${action} osobu`,
      content: active
        ? `Naozaj chcete vyradiť osobu „${personName}“? Údaje zostanú zachované, ale úpravy, termíny a upozornenia budú pozastavené.`
        : `Naozaj chcete zaradiť osobu „${personName}“? Úpravy, termíny a upozornenia budú opäť dostupné.`,
      primaryButtonText: action,
      secondaryButtonText: 'Zrušiť',
      onConfirm: () => setStatusRemote(active ? 'inactive' : 'active'),
    });
  }, [active, confirm, personName, setStatusRemote]);

  if (!canView) {
    return null;
  }

  return (
    <ResponsiveHrActions
      primary={{
        label: canEdit ? 'Upraviť' : 'Zobraziť',
        icon: canEdit ? <IconEdit size={20} stroke={1.5} /> : <IconEye size={20} stroke={1.5} />,
        disabled: canEdit && !active,
        to: path,
      }}
      ariaLabel={`${canEdit ? 'Upraviť' : 'Zobraziť'} osobu ${personName}`}
      menuLabel={personName}
    >
      {canEdit && (
        <Menu.Item component={Link} to={copyPath} icon={<IconCopy size={20} stroke={1.5} />}>
          Kopírovať
        </Menu.Item>
      )}
      {canEdit && (
        <Menu.Item
          color="red.8"
          icon={active ? <IconTrashX size={20} stroke={1.5} /> : <IconBackspace size={20} stroke={1.5} />}
          onClick={confirmSetStatus}
        >
          {active ? 'Vyradiť' : 'Zaradiť'}
        </Menu.Item>
      )}
    </ResponsiveHrActions>
  );
}

interface HrTermActionsProps {
  data: any;
  onArchive: (term: any) => void;
  onExecute: (term: any) => void;
  onReactivate?: (term: any) => void;
  showDetail?: boolean;
  detailAsPrimary?: boolean;
  hideDetailDestination?: boolean;
  onPairCertificate?: (term: any) => void;
  onUnpairCertificate?: (term: any) => void;
}

/** Global table/calendar actions with a read-only Detail destination in every requested context. */
export function HrTermActions({
  data,
  onArchive,
  onExecute,
  onReactivate,
  showDetail = true,
  detailAsPrimary = false,
  hideDetailDestination = false,
  onPairCertificate,
  onUnpairCertificate,
}: HrTermActionsProps) {
  const personPath = HR_EDIT_PERSON_PAGE_PATH.insert({ personId: String(data.personId) });
  const editPersonPath = `${personPath}?highlightTermId=${data.termId}`;
  const detailPath = HR_TERM_PAGE_PATH.insert({ termId: String(data.termId) });
  const status = data.rawStatus ?? data.status;
  const active = isActiveRow(data);
  const archived = status === 'archived';
  const completed = status === 'completed';
  const canEdit = Boolean(data.actionPermissions?.edit);
  const canExecute = Boolean(data.actionPermissions?.execute) && active;
  const canPair = active && data.kind === 'technician-certificate' && !data.certificateId && canEdit;
  const canUnpair = active && Boolean(data.certificateId) && canEdit;
  const canReactivate = archived && canEdit && Boolean(onReactivate);
  const detailIsPrimary = !hideDetailDestination && (detailAsPrimary || completed);
  const primary = canExecute
    ? {
        label: 'Vykonať',
        icon: <IconPlayerPlay size={20} stroke={1.5} />,
        onClick: () => onExecute(data),
      }
    : detailIsPrimary
    ? {
        label: 'Detail',
        icon: <IconEye size={20} stroke={1.5} />,
        to: detailPath,
      }
    : canEdit && !completed
    ? {
        label: 'Upraviť',
        icon: <IconEdit size={20} stroke={1.5} />,
        disabled: !active,
        to: editPersonPath,
      }
    : {
        label: 'Osoba',
        icon: <IconEye size={20} stroke={1.5} />,
        to: personPath,
      };

  const personName = `${data.firstName ?? ''} ${data.lastName ?? ''}`.trim();
  const termName = data.termTypeName ?? 'Termín';
  const termLabel = personName ? `${termName} – ${personName}` : `${termName} (ID ${data.termId})`;
  return (
    <ResponsiveHrActions
      primary={primary}
      ariaLabel={`Ďalšie akcie: ${termLabel}${personName ? ` (ID ${data.termId})` : ''}`}
      menuLabel={termLabel}
    >
      {showDetail && !hideDetailDestination && (canExecute || !detailIsPrimary) && (
        <Menu.Item icon={<IconEye size={20} stroke={1.5} />} component={Link} to={detailPath}>
          Detail
        </Menu.Item>
      )}
      {(canExecute || detailIsPrimary) &&
        canEdit &&
        !completed &&
        (active ? (
          <Menu.Item icon={<IconEdit size={20} stroke={1.5} />} component={Link} to={editPersonPath}>
            Upraviť
          </Menu.Item>
        ) : (
          <Menu.Item icon={<IconEdit size={20} stroke={1.5} />} disabled>
            Upraviť
          </Menu.Item>
        ))}
      {(canExecute || detailIsPrimary) && !canEdit && (
        <Menu.Item icon={<IconEye size={20} stroke={1.5} />} component={Link} to={personPath}>
          Osoba
        </Menu.Item>
      )}
      {canPair && onPairCertificate && (
        <Menu.Item icon={<IconLink size={20} stroke={1.5} />} onClick={() => onPairCertificate(data)}>
          Spárovať osvedčenie
        </Menu.Item>
      )}
      {canUnpair && onUnpairCertificate && (
        <Menu.Item color="red.8" icon={<IconUnlink size={20} stroke={1.5} />} onClick={() => onUnpairCertificate(data)}>
          Odpojiť osvedčenie
        </Menu.Item>
      )}
      {canReactivate && (
        <Menu.Item icon={<IconRefresh size={20} stroke={1.5} />} onClick={() => onReactivate?.(data)}>
          Zaradiť
        </Menu.Item>
      )}
      {canEdit && active && (
        <Menu.Item color="red.8" icon={<IconArchive size={20} stroke={1.5} />} onClick={() => onArchive(data)}>
          Vyradiť
        </Menu.Item>
      )}
    </ResponsiveHrActions>
  );
}

/** Configuration-table actions with edit as the primary action. */
export function HrConfigurationActions({
  data,
  node,
  editTarget,
  entityNameAccusative,
  onToggle,
}: {
  data: any;
  node: IRowNode<any>;
  editTarget: (item: any) => string;
  entityNameAccusative: string;
  onToggle: (item: any, node: IRowNode<any>) => void | Promise<void>;
}) {
  const { confirm } = useConfirm();
  const active = isActiveRow(data);
  const configurationLabel = data.name ?? `Konfigurácia ID ${data.termTypeId ?? data.categoryId}`;
  const action = active ? 'Vyradiť' : 'Zaradiť';

  /** Confirms the retained lifecycle transition before persisting it. */
  const confirmToggle = useCallback(() => {
    confirm({
      title: `${action} ${entityNameAccusative}`,
      content: `Naozaj chcete ${active ? 'vyradiť' : 'zaradiť'} ${entityNameAccusative} „${configurationLabel}“?`,
      onConfirm: () => onToggle(data, node),
    });
  }, [action, active, configurationLabel, confirm, data, entityNameAccusative, node, onToggle]);

  return (
    <ResponsiveHrActions
      primary={{
        label: 'Upraviť',
        icon: <IconEdit size={20} stroke={1.5} />,
        disabled: !active,
        to: editTarget(data),
      }}
      ariaLabel={`Ďalšie akcie konfigurácie ${data.name ?? data.termTypeId ?? data.categoryId}`}
      menuLabel={configurationLabel}
    >
      <Menu.Item
        color={active ? 'red.8' : undefined}
        icon={active ? <IconArchive size={20} stroke={1.5} /> : <IconRefresh size={20} stroke={1.5} />}
        onClick={confirmToggle}
      >
        {action}
      </Menu.Item>
    </ResponsiveHrActions>
  );
}

interface HrPersonTermActionsProps {
  data: any;
  inactive: boolean;
  readonly?: boolean;
  openTermModal: (mode: 'execute' | 'pair' | 'reactivate', term: any) => void;
  unpairCertificate: (term: any) => void;
  setTermStatus: (term: any) => void;
}

/** Canonical person-page term actions with execute or read-only detail as the primary action. */
export function HrPersonTermActions(props: HrPersonTermActionsProps) {
  const { data, inactive, readonly = false, openTermModal, unpairCertificate, setTermStatus } = props;
  const status = data.rawStatus ?? data.status;
  const canExecute = !readonly && status === 'active' && data.actionPermissions.execute && !inactive;
  const canEdit = !readonly && status === 'active' && data.actionPermissions.edit && !inactive;
  const canPair = data.kind === 'technician-certificate' && !data.certificateId && canEdit;
  const primary = canExecute
    ? {
        label: 'Vykonať',
        icon: <IconPlayerPlay size={20} stroke={1.5} />,
        onClick: () => openTermModal('execute', data),
      }
    : {
        label: 'Detail',
        icon: <IconEye size={20} stroke={1.5} />,
        to: HR_TERM_PAGE_PATH.insert({ termId: String(data.termId) }),
      };
  const termLabel = data.termTypeName ?? `Termín ID ${data.termId}`;

  return (
    <ResponsiveHrActions
      primary={primary}
      ariaLabel={`Ďalšie akcie: ${data.termTypeName ?? 'Termín'} (ID ${data.termId})`}
      menuLabel={termLabel}
    >
      {canPair && (
        <Menu.Item icon={<IconLink size={20} stroke={1.5} />} onClick={() => openTermModal('pair', data)}>
          Spárovať osvedčenie
        </Menu.Item>
      )}
      {!readonly && data.certificateId && data.actionPermissions.edit && !inactive && (
        <Menu.Item color="red.8" icon={<IconUnlink size={20} stroke={1.5} />} onClick={() => unpairCertificate(data)}>
          Odpojiť
        </Menu.Item>
      )}
      {!readonly && data.actionPermissions.edit && !inactive && (status === 'active' || status === 'archived') && (
        <Menu.Item
          color={status === 'active' ? 'red.8' : undefined}
          icon={status === 'active' ? <IconArchive size={20} stroke={1.5} /> : <IconRefresh size={20} stroke={1.5} />}
          onClick={() => setTermStatus(data)}
        >
          {status === 'active' ? 'Vyradiť' : 'Zaradiť'}
        </Menu.Item>
      )}
      {canExecute && (
        <Menu.Item
          icon={<IconEye size={20} stroke={1.5} />}
          component={Link}
          to={HR_TERM_PAGE_PATH.insert({ termId: String(data.termId) })}
        >
          Detail
        </Menu.Item>
      )}
    </ResponsiveHrActions>
  );
}
